import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import EditIcon from "@mui/icons-material/Edit";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buscarApuracaoDetalhe,
  buscarClassificacaoNcmApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import BadgeStatus from "../components/BadgeStatus";
import SelectFiltro, { FILTER_FIELD_SX } from "../components/SelectFiltro";
import AbaEmBreve from "../DetalheIcms/AbaEmBreve";
import { formatarCnpj, sumarizacaoEmAndamento } from "../utils";
import {
  CLASSIFICACOES,
  classificacaoCliente,
  definirClassificacaoCliente,
} from "./icmsClassificacaoClienteStore";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const COD_RELATORIO_CAT42 = "ICMS_ST_CAT42";

/** Mesmo formato do protótipo (apuracao_icms_classificacao_ncm.html): 2 casas, vírgula
 * decimal, "—" quando não há valor — nunca 0,00% (0 é um MVA/alíquota válido, "sem dado"
 * é outra coisa). */
function formatarPercentual(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return (
    Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%"
  );
}

const CLASSIFICACAO_OPCOES = [
  { value: "", label: "Todas" },
  { value: "Insumo", label: "Insumo" },
  { value: "Uso", label: "Uso" },
  { value: "Consumo", label: "Consumo" },
  { value: "Não classificado", label: "Não classificado" },
];

/** Cores por classificação do protótipo (apuracao_icms_classificacao_ncm.html,
 * .badge-classificacao.insumo/uso/consumo) — usadas tanto na Classificação sugerida quanto
 * na Classificação do cliente, já que as duas colunas mostram o mesmo domínio de valores. */
const COR_POR_CLASSIFICACAO = {
  Insumo: { bg: "#e8f5e9", cor: "#2e7d32" },
  Uso: { bg: "#fff3e0", cor: "#ad4e00" },
  Consumo: { bg: "#e3f2fd", cor: "#1565c0" },
};

function BadgeClassificacao({ valor }) {
  const cores = COR_POR_CLASSIFICACAO[valor];
  if (!cores) return <BadgeStatus tom="neutro">{valor}</BadgeStatus>;
  return (
    <Box
      component="span"
      sx={{ display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap", bgcolor: cores.bg, color: cores.cor }}
    >
      {valor}
    </Box>
  );
}

function formatarNcm(ncm) {
  const digitos = String(ncm || "").replace(/\D/g, "");
  if (digitos.length !== 8) return ncm || "—";
  return `${digitos.slice(0, 4)}.${digitos.slice(4, 6)}.${digitos.slice(6, 8)}`;
}

/**
 * TXM-472 — "Classificação NCM" do cabeçalho geral da Apuração ICMS. `id` (useParams) é a
 * apuração ICMS PAI: a tela resolve a filha ICMS_ST_CAT42 sozinha (como o card manda) porque
 * é lá que vivem os NCMs, não no pai — o mesmo salto que DetalheIcms/index.js já faz pra
 * montar a aba "ICMS Cat-42". Reconstruímos a URL de voltar em vez de navigate(-1) porque a
 * tela precisa funcionar em link direto/refresh, não só vindo de um clique (mesmo motivo de
 * LancamentosIcmsE111).
 *
 * "Classificação do cliente" (ajuste manual por CNPJ+NCM, botão "Editar") segue o MESMO
 * padrão já usado na classificação por nota do Ecredac (LancamentosEcredac/
 * ecredacClassificacaoStore.js): override salvo no localStorage, com prioridade sobre a
 * sugestão do sistema. A sugestão de base (Tipo, Classificação sugerida e Valor MVA) vem da
 * Tabela NCM (TXM-594) — cadastro mais específico para o CNPJ da apuração, resolvido no banco
 * pela function public.fn_tb_ncm_item_vigente, a mesma que os motores usam; NCM sem
 * classificação lá aparece como "Não classificado". O ajuste manual por cliente continua
 * standalone (ver icmsClassificacaoClienteStore.js) e não grava na Tabela NCM.
 */
export default function ClassificacaoNcm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A aba de origem chega em ?tab= (ver botão "Classificação NCM" em DetalheIcms/index.js,
  // que passa TAB_SLUGS[abaAtivaIndex]) — "icms" só é usado em link direto/refresh, sem essa
  // query string.
  const tabOrigem = searchParams.get("tab") || "icms";

  const [loadingApuracao, setLoadingApuracao] = useState(true);
  const [erroApuracao, setErroApuracao] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [filhoCat42Id, setFilhoCat42Id] = useState(undefined); // undefined = ainda resolvendo; null = não existe

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [busca, setBusca] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [classificacaoFiltro, setClassificacaoFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);
  const [edicao, setEdicao] = useState(null);
  const [novaClassificacaoCliente, setNovaClassificacaoCliente] = useState("");
  // classificacaoCliente() lê localStorage fora do estado do React (mesmo mecanismo do
  // Ecredac) — versaoClienteOverride só existe pra forçar o recálculo de linhasComCliente
  // depois de um Salvar no modal.
  const [versaoClienteOverride, setVersaoClienteOverride] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    let ativo = true;
    async function carregarApuracao() {
      setLoadingApuracao(true);
      setErroApuracao("");
      const res = await buscarApuracaoDetalhe(id);
      if (!ativo) return;
      if (!res.sucesso) {
        setErroApuracao(res.mensagem || "Apuração não encontrada.");
        setLoadingApuracao(false);
        return;
      }
      setApuracao(res.apuracao || null);
      const filha = (res.filhos || []).find((f) => f.codRelatorio === COD_RELATORIO_CAT42);
      setFilhoCat42Id(filha?.id ?? null);
      setLoadingApuracao(false);
    }
    carregarApuracao();
    return () => {
      ativo = false;
    };
  }, [id]);

  useEffect(() => {
    if (!filhoCat42Id) return;
    let ativo = true;
    async function carregarClassificacao() {
      setLoading(true);
      setErro("");
      const res = await buscarClassificacaoNcmApuracao(filhoCat42Id);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Não foi possível carregar a classificação NCM.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setAvisos(res.avisos || []);
      setLoading(false);
    }
    carregarClassificacao();
    return () => {
      ativo = false;
    };
  }, [filhoCat42Id]);

  const tipoOpcoes = useMemo(() => {
    const tipos = Array.from(new Set(linhas.map((l) => l.tipo).filter(Boolean))).sort();
    return [{ value: "", label: "Todos" }, ...tipos.map((t) => ({ value: t, label: t }))];
  }, [linhas]);

  const linhasComCliente = useMemo(
    () => linhas.map((l) => ({ ...l, classificacaoCliente: classificacaoCliente(apuracao?.cnpj, l.ncm) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linhas, apuracao, versaoClienteOverride]
  );

  const linhasFiltradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return linhasComCliente.filter((l) => {
      if (tipoFiltro && l.tipo !== tipoFiltro) return false;
      if (classificacaoFiltro) {
        const sugerida = l.classificacaoSugerida || "Não classificado";
        if (sugerida !== classificacaoFiltro) return false;
      }
      if (!q) return true;
      return (
        String(l.ncm || "").toLowerCase().includes(q) ||
        String(l.item || "").toLowerCase().includes(q) ||
        String(l.cest || "").toLowerCase().includes(q) ||
        String(l.descricao || "").toLowerCase().includes(q)
      );
    });
  }, [linhasComCliente, busca, tipoFiltro, classificacaoFiltro]);

  function limparFiltros() {
    setBusca("");
    setTipoFiltro("");
    setClassificacaoFiltro("");
  }

  function abrirEdicao(linha) {
    setEdicao(linha);
    setNovaClassificacaoCliente(linha.classificacaoCliente || "");
  }

  function salvarEdicao() {
    definirClassificacaoCliente(apuracao?.cnpj, edicao.ncm, novaClassificacaoCliente);
    setEdicao(null);
    setVersaoClienteOverride((v) => v + 1);
    setSnack({
      open: true,
      msg: novaClassificacaoCliente
        ? `Classificação do cliente salva para o NCM ${formatarNcm(edicao.ncm)}.`
        : "Classificação específica removida — voltando à sugestão do sistema.",
      severity: "success",
    });
  }

  function handleVoltar() {
    navigate(`/relatorios/apuracoes/${id}/detalhe/icms?tab=${tabOrigem}`);
  }

  if (loadingApuracao) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  const itensBreadcrumb = [
    { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
    { label: `Apuração ICMS — ${apuracao?.empresa || "—"}`, onClick: handleVoltar },
    { label: "Classificação NCM" },
  ];

  if (erroApuracao) {
    return (
      <BreadcrumbApuracao itens={itensBreadcrumb}>
        <Alert severity="error">{erroApuracao}</Alert>
      </BreadcrumbApuracao>
    );
  }

  return (
    <BreadcrumbApuracao itens={itensBreadcrumb}>
      <>
        {filhoCat42Id === null ? (
          <AbaEmBreve
            titulo="Apuração CAT-42 vinculada"
            descricao="Os NCMs desta apuração vêm da apuração CAT-42 — nenhuma foi encontrada para este registro."
          />
        ) : (
          <>
            {avisos.map((aviso, i) => (
              <Alert severity="info" sx={{ mb: 2 }} key={i}>
                {aviso}
              </Alert>
            ))}

            {erro &&
              (sumarizacaoEmAndamento(erro) ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Sumarização em processamento — os NCMs aparecerão aqui assim que for concluída.
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {erro}
                </Alert>
              ))}

            {!erro && (
              <ApuracaoCardShell
                hideContainer
                innerCardSx={{ border: "none", margin: 0 }}
                conteudoComScrollProprio
                Icone={CategoryIcon}
                titulo="Classificação NCM"
                subtitulo={
                  <>
                    {apuracao?.empresa || "—"} · {formatarCnpj(apuracao?.cnpj)}
                  </>
                }
                acaoHeader={
                  <p style={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap", margin: 0 }}>
                    {linhasFiltradas.length} NCM(s){" "}
                    {linhasFiltradas.length !== linhas.length ? `(filtrado de ${linhas.length})` : ""}
                  </p>
                }
                filtros={
                  <>
                    <TextField
                      size="small"
                      placeholder="Item, CEST, NCM/SH ou Descrição"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      sx={{ ...FILTER_FIELD_SX, width: 280 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#3c4043" }} fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <SelectFiltro label="Tipo" value={tipoFiltro} onChange={setTipoFiltro} options={tipoOpcoes} />
                    <SelectFiltro label="Classificação sugerida" value={classificacaoFiltro} onChange={setClassificacaoFiltro} options={CLASSIFICACAO_OPCOES} />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={limparFiltros}
                      sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
                    >
                      Limpar filtros
                    </Button>
                  </>
                }
                paginacao={
                  !loading &&
                  linhasFiltradas.length > 0 && (
                    <Pagination
                      paginationKey="classificacao-ncm"
                      data={linhasFiltradas}
                      filteredData={linhasPagina}
                      setFilteredData={setLinhasPagina}
                    />
                  )
                }
              >
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <GridLoader color="#1565c0" size={10} />
                  </Box>
                ) : linhasFiltradas.length === 0 ? (
                  <p className={styles.emptyMsg}>Nenhum NCM encontrado para este filtro.</p>
                ) : (
                    <div style={{ margin: "8px 0 0", overflow: "auto" }}>
                      <table className={tableStyles.accordion_table}>
                        <thead>
                          <tr>
                            <th scope="col">Item</th>
                            <th scope="col">CEST</th>
                            <th scope="col">NCM/SH</th>
                            <th scope="col">Descrição</th>
                            <th scope="col">Tipo</th>
                            <th scope="col">Classificação</th>
                            <th scope="col" style={{ textAlign: "right" }}>Valor MVA</th>
                            <th scope="col">Classificação do cliente</th>
                            <th scope="col" style={{ textAlign: "center" }}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linhasPagina.map((l) => (
                            <tr key={l.ncm}>
                              <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.item || "—"}</td>
                              <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.cest || "—"}</td>
                              <td style={{ fontFamily: "ui-monospace, monospace" }}>{formatarNcm(l.ncm)}</td>
                              <td>{l.descricao || "—"}</td>
                              <td>{l.tipo || "—"}</td>
                              <td>
                                {l.classificacaoSugerida ? (
                                  <BadgeClassificacao valor={l.classificacaoSugerida} />
                                ) : (
                                  <Tooltip title="NCM sem classificação na Tabela NCM para este cliente — cadastre em Parametrizações › Tabela NCM">
                                    <span>
                                      <BadgeStatus tom="neutro">Não classificado</BadgeStatus>
                                    </span>
                                  </Tooltip>
                                )}
                              </td>
                              <td style={{ textAlign: "right", fontFamily: "ui-monospace, monospace" }}>
                                {l.valorMva == null ? (
                                  <Tooltip title="Sem MVA cadastrado na Tabela NCM para este NCM">
                                    <span>—</span>
                                  </Tooltip>
                                ) : (
                                  formatarPercentual(l.valorMva)
                                )}
                              </td>
                              <td>
                                {l.classificacaoCliente ? (
                                  <>
                                    <BadgeClassificacao valor={l.classificacaoCliente} />{" "}
                                    <span style={{ marginLeft: 4, padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700, textTransform: "uppercase", background: "#f5f5f5", color: "#777" }}>
                                      cliente
                                    </span>
                                  </>
                                ) : (
                                  <BadgeStatus tom="neutro">Sugestão do sistema</BadgeStatus>
                                )}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <IconButton
                                  size="small"
                                  title="Editar classificação do cliente"
                                  onClick={() => abrirEdicao(l)}
                                  sx={{ color: "#18316e" }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                )}
              </ApuracaoCardShell>
            )}
          </>
        )}

        <Dialog open={!!edicao} onClose={() => setEdicao(null)} fullWidth maxWidth="sm">
          <DialogTitle>Classificação específica do cliente</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 0.5 }}>
              <strong>NCM/SH:</strong> {edicao ? formatarNcm(edicao.ncm) : "—"}
            </Typography>
            <Typography sx={{ mb: 2 }}>
              <strong>Classificação sugerida (Tabela NCM):</strong>{" "}
              {edicao?.classificacaoSugerida || "Não classificado"}
            </Typography>
            <TextField
              select
              fullWidth
              label="Classificação do cliente"
              value={novaClassificacaoCliente}
              onChange={(e) => setNovaClassificacaoCliente(e.target.value)}
            >
              <MenuItem value="">Usar sugestão do sistema</MenuItem>
              {CLASSIFICACOES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <Alert severity="info" sx={{ mt: 2 }}>
              Vale apenas para o CNPJ desta apuração e tem prioridade sobre a sugestão do
              sistema.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEdicao(null)}>Cancelar</Button>
            <Button variant="contained" onClick={salvarEdicao} sx={{ bgcolor: "#18316e", "&:hover": { bgcolor: "#152858" } }}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert severity={snack.severity}>{snack.msg}</Alert>
        </Snackbar>
      </>
    </BreadcrumbApuracao>
  );
}
